import type {
  RateHawkRate,
  RateHawkRgExt,
  RateHawkRoomGroup,
  RateHawkRoomGuest,
  RateHawkRoomGuestConfig,
  RateHawkSearchResult,
  RateHawkPaymentType,
} from "../types";
import type { AccommodationActivity } from "../../models/activity.model";
import { resolveWorldotaUrl } from "../WorldotaImage";
import { toMediaItem } from "@/types/media.types";

// ─── Guest helpers ────────────────────────────────────────────────────────────

/**
 * Build the RateHawk guests array (one entry per room) from independently
 * edited per-room configs. Each room keeps its own adults count and child ages.
 */
export function buildRateHawkGuests(
  rooms: RateHawkRoomGuestConfig[],
): RateHawkRoomGuest[] {
  return rooms.map((room) => ({
    adults: Math.max(1, room.adults),
    children: room.childrenAges,
  }));
}

/**
 * Parse the guest count from a stored guests array (reverse of buildRateHawkGuests).
 * Returns the first room's counts (multi-room setups share the same config here).
 */
export function countGuestsFromRateHawkData(
  guestsData?: { age?: number }[],
  totalGuests?: number,
): { adults: number; children: number; childrenAges: number[] } {
  if (guestsData && guestsData.length > 0) {
    const childrenAges: number[] = [];
    let adults = 0;
    for (const g of guestsData) {
      const age = typeof g.age === "number" ? g.age : 18;
      if (age >= 18) {
        adults++;
      } else {
        childrenAges.push(age);
      }
    }
    return { adults: adults || 1, children: childrenAges.length, childrenAges };
  }
  return { adults: totalGuests || 2, children: 0, childrenAges: [] };
}

// ─── Formatting ───────────────────────────────────────────────────────────────

export function formatCurrency(amount: string | undefined, currency: string | undefined): string {
  if (!amount) return "-";
  const num = parseFloat(amount);
  if (isNaN(num)) return amount;
  const curr = currency || "EUR";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: curr }).format(num);
  } catch {
    return `${curr} ${num.toFixed(2)}`;
  }
}

/**
 * Map RateHawk meal codes to human-readable board-type labels.
 *
 * RateHawk meal codes:
 *   nomeal | breakfast | breakfast_for_one | dinner | lunch |
 *   halfboard | fullboard | all-inclusive | soft-all-inclusive |
 *   ultra-all-inclusive | super-all-inclusive
 *
 */
export function mealToTag(meal?: string): string {
  if (!meal) return "Only accommodation";
  const map: Record<string, string> = {
    nomeal: "Only accommodation",
    breakfast: "Breakfast Included",
    breakfast_for_one: "Breakfast for One",
    lunch: "Lunch Included",
    dinner: "Dinner Included",
    halfboard: "Half board",
    half_board: "Half board",           // defensive alias
    fullboard: "Full board",
    full_board: "Full board",           // defensive alias
    "all-inclusive": "All inclusive",
    "soft-all-inclusive": "Soft All inclusive",
    "ultra-all-inclusive": "Ultra All inclusive",
    "super-all-inclusive": "Super All inclusive",
  };
  return map[meal] ?? "Only accommodation";
}

/**
 * Format the beds description from RateHawkRoomDataTrans.beds.
 * e.g. [{ bed: "double", count: 1 }] → "1x double"
 */
export function formatBeds(beds?: { bed: string; count: number }[]): string {
  if (!beds || beds.length === 0) return "";
  return beds.map((b) => `${b.count}x ${b.bed}`).join(", ");
}

// ─── Cancellation Analysis ────────────────────────────────────────────────────

export type CancellationLabel = "free" | "partial" | "non-refundable";
export type RateBadge = "best-price" | "flexible" | "best-deal" | null;

export interface RateInsight {
  rateId: string;
  isCheapest: boolean;
  /** Absolute difference from the cheapest show_amount in the room group. */
  priceDiff: number;
  priceDiffFormatted: string;
  cancellationLabel: CancellationLabel;
  /** ISO datetime string of the free-cancellation deadline, or null. */
  cancellationDeadline: string | null;
  /** Formatted deadline date for UI display. */
  cancellationDeadlineFormatted: string | null;
  refundFormatted: string | null;
  refundPercentage: number | null;
  badge: RateBadge;
  comparisonNote: string | null;
}

function parseAmount(value?: string): number {
  if (!value) return NaN;
  const n = parseFloat(value);
  return isNaN(n) ? NaN : n;
}

/**
 * Get the primary payment type for a rate.
 * RateHawk may return multiple payment_types but the first is authoritative.
 */
function primaryPaymentType(rate: RateHawkRate): RateHawkPaymentType | undefined {
  return rate.payment_options?.payment_types?.[0];
}

/**
 * Resolve the display amount for a rate, preferring the top-level show_amount
 * (present in SERP responses) over the nested payment_options field.
 */
function resolveAmount(rate: RateHawkRate): number {
  const top = parseAmount(rate.show_amount);
  if (!isNaN(top)) return top;
  return parseAmount(primaryPaymentType(rate)?.show_amount);
}

/**
 * RateHawk's Hotelpage response nests tax_data inside
 * payment_options.payment_types[0] rather than at the top level of the rate
 * (confirmed against captured real responses) — check both locations,
 * preferring the top-level field if a future response variant sets it there.
 */
export function resolveTaxData(
  rate: RateHawkRate,
): { taxes: Array<{ name: string; amount: string; currency_code: string; included_by_supplier: boolean }> } | null {
  return rate.tax_data ?? primaryPaymentType(rate)?.tax_data ?? null;
}

function classifyCancellation(rate: RateHawkRate): {
  label: CancellationLabel;
  deadline: string | null;
  refundAmount: number;
  refundPercentage: number | null;
  refundCurrency: string | null;
} {
  const pt = primaryPaymentType(rate);
  if (!pt) {
    return { label: "non-refundable", deadline: null, refundAmount: 0, refundPercentage: null, refundCurrency: null };
  }

  const freeBefore = pt.cancellation_penalties.free_cancellation_before;
  if (freeBefore) {
    // There is a free-cancellation window → "free" or "partial"
    const totalShow = parseAmount(pt.show_amount);
    if (!isNaN(totalShow) && totalShow > 0) {
      return {
        label: "free",
        deadline: freeBefore,
        refundAmount: totalShow,
        refundPercentage: 100,
        refundCurrency: pt.show_currency_code,
      };
    }
    return { label: "free", deadline: freeBefore, refundAmount: 0, refundPercentage: 100, refundCurrency: pt.show_currency_code };
  }

  // Check policies for a partial refund window
  const policies = pt.cancellation_penalties.policies ?? [];
  const total = parseAmount(pt.show_amount);
  let bestRefund = 0;
  let bestDeadline: string | null = null;
  let refundCurrency: string | null = pt.show_currency_code;

  for (const policy of policies) {
    const charge = parseAmount(policy.amount_show);
    if (!isNaN(charge) && !isNaN(total) && total > 0) {
      const refund = total - charge;
      if (refund > bestRefund) {
        bestRefund = refund;
        bestDeadline = policy.end_at;
      }
    }
  }

  if (isNaN(total) || total === 0 || bestRefund === 0) {
    return { label: "non-refundable", deadline: null, refundAmount: 0, refundPercentage: null, refundCurrency };
  }

  const ratio = bestRefund / total;
  const label: CancellationLabel = ratio >= 0.9 ? "free" : "partial";
  return {
    label,
    deadline: label !== "non-refundable" ? bestDeadline : null,
    refundAmount: bestRefund,
    refundPercentage: Math.round(ratio * 100),
    refundCurrency,
  };
}

function formatDeadline(iso: string | null): string | null {
  if (!iso) return null;
  try {
    // RateHawk sends this as a timezone-naive string (e.g. "2026-07-15T12:00:00")
    // representing UTC. The Date constructor treats a datetime string with no
    // zone/offset as LOCAL time per the ECMAScript spec, which silently shifts
    // the displayed value by the viewer's UTC offset. Force UTC parsing when
    // no zone marker is present.
    const hasZone = /Z$|[+-]\d{2}:\d{2}$/.test(iso);
    const d = new Date(hasZone ? iso : `${iso}Z`);
    if (isNaN(d.getTime())) return null;
    const date = d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
    const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "UTC" });
    return `${date}, ${time} UTC`;
  } catch {
    return null;
  }
}

/**
 * Analyse all rates within one room group and produce comparison insights.
 * Returns a Map keyed by match_hash for O(1) lookups.
 */
export function analyzeRates(rates: RateHawkRate[]): Map<string, RateInsight> {
  const result = new Map<string, RateInsight>();
  if (rates.length === 0) return result;

  // Determine show amounts for each rate (top-level show_amount preferred over nested)
  const amounts = rates.map((r) => resolveAmount(r));
  const validAmounts = amounts.filter((a) => !isNaN(a));
  const minAmount = validAmounts.length > 0 ? Math.min(...validAmounts) : NaN;
  const dominantCurrency = primaryPaymentType(rates[0])?.show_currency_code ?? "EUR";

  // Count refundable rates for badge logic
  const cancellationLabels = rates.map((r) => classifyCancellation(r).label);
  const hasFlexible = cancellationLabels.some((l) => l === "free" || l === "partial");

  for (let i = 0; i < rates.length; i++) {
    const rate = rates[i];
    const amount = amounts[i];
    const isCheapest = !isNaN(amount) && !isNaN(minAmount) && amount === minAmount;
    const priceDiff = !isNaN(amount) && !isNaN(minAmount) ? amount - minAmount : 0;

    const { label, deadline, refundAmount, refundPercentage, refundCurrency } =
      classifyCancellation(rate);

    const refundFormatted =
      refundAmount > 0
        ? formatCurrency(refundAmount.toFixed(2), refundCurrency ?? dominantCurrency)
        : null;

    // Badge logic
    let badge: RateBadge = null;
    if (isCheapest && (label === "free" || label === "partial")) {
      badge = "best-deal";
    } else if (isCheapest) {
      badge = "best-price";
    } else if ((label === "free" || label === "partial") && !hasFlexible) {
      badge = "flexible";
    }

    let comparisonNote: string | null = null;
    if (priceDiff > 0) {
      comparisonNote = `+${formatCurrency(priceDiff.toFixed(2), dominantCurrency)} vs. cheapest`;
    }

    result.set(rate.match_hash, {
      rateId: rate.match_hash,
      isCheapest,
      priceDiff,
      priceDiffFormatted: priceDiff > 0 ? `+${formatCurrency(priceDiff.toFixed(2), dominantCurrency)}` : "",
      cancellationLabel: label,
      cancellationDeadline: deadline,
      cancellationDeadlineFormatted: formatDeadline(deadline),
      refundFormatted,
      refundPercentage,
      badge,
      comparisonNote,
    });
  }

  return result;
}

// ─── Room Grouping ────────────────────────────────────────────────────────────

const RG_EXT_FIELDS = [
  "class", "quality", "sex", "bathroom", "bedding", "family",
  "capacity", "club", "bedrooms", "balcony", "view", "floor",
] as const;

function rgExtKey(rg: RateHawkRgExt): string {
  return RG_EXT_FIELDS.map((f) => `${f}:${rg[f] ?? 0}`).join(",");
}

/**
 * Group a flat list of RateHawk rates (from Hotelpage) into RateHawkRoomGroup[].
 *
 * Rates are grouped by rg_ext (stable room-type identifier from ETG).
 * Rates without rg_ext fall back to grouping by room_name.
 * Photos come from room_photos attached by the edge function via static data matching.
 * Within each group, rates are sorted by show_amount ascending.
 */
export function groupRatesByRoom(rates: RateHawkRate[]): RateHawkRoomGroup[] {
  const map = new Map<string, RateHawkRate[]>();

  for (const rate of rates) {
    const key = rate.rg_ext ? rgExtKey(rate.rg_ext) : rate.room_name;
    const bucket = map.get(key) ?? [];
    bucket.push(rate);
    map.set(key, bucket);
  }

  const groups: RateHawkRoomGroup[] = [];

  for (const [, roomRates] of map) {
    // Sort by show_amount ascending
    const sorted = [...roomRates].sort((a, b) => {
      const aAmt = resolveAmount(a);
      const bAmt = resolveAmount(b);
      if (isNaN(aAmt)) return 1;
      if (isNaN(bAmt)) return -1;
      return aAmt - bAmt;
    });

    const first = sorted[0];
    const beds = formatBeds(first.room_data_trans?.beds);

    groups.push({
      roomName: first.room_name,
      bedsLabel: beds,
      amenities: first.amenities_data ?? [],
      photos: first.room_photos ?? [],
      rates: sorted,
    });
  }

  // Sort groups by cheapest rate ascending
  groups.sort((a, b) => {
    const aAmt = resolveAmount(a.rates[0]);
    const bAmt = resolveAmount(b.rates[0]);
    if (isNaN(aAmt)) return 1;
    if (isNaN(bAmt)) return -1;
    return aAmt - bAmt;
  });

  return groups;
}

// ─── RateHawk → Place2Be Converter ───────────────────────────────────────────

interface ConvertRateHawkParams {
  rate: RateHawkRate;
  room: RateHawkRoomGroup;
  hotel: RateHawkSearchResult;
  checkInDate: string;
  checkOutDate: string;
  selectedRooms: RateHawkRoomGuestConfig[];
}

/**
 * Convert a selected RateHawk rate + hotel into a partial AccommodationActivity
 * ready to be saved to the itinerary.
 *
 * Field mapping (RateHawk → AccommodationActivity):
 *
 *   hotel.name                                    → title prefix
 *   room.roomName                                 → title suffix
 *   hotel.address + hotel.cityName               → address
 *   hotel.photos                                  → media
 *   hotel.checkInTime                             → check_in_time
 *   hotel.checkOutTime                            → check_out_time
 *   primaryPaymentType(rate).show_amount          → cost
 *   primaryPaymentType(rate).show_currency_code   → (used for formatted price_tag)
 *   free_cancellation_before != null              → refundable_rate
 *   rate.meal                                     → price_tag (via mealToTag)
 *   checkInDate                                   → start_date
 *   checkOutDate                                  → end_date
 *   room.bedsLabel + rate.amenities_data          → description fragments
 */
export function convertRateHawkToPlace2BeAccommodation({
  rate,
  room,
  hotel,
  checkInDate,
  checkOutDate,
  selectedRooms,
}: ConvertRateHawkParams): Partial<AccommodationActivity> {
  const pt = primaryPaymentType(rate);
  const totalAmount = parseAmount(pt?.show_amount) || 0;

  // Cancellation: refundable if there is any free cancellation window
  const freeBefore = pt?.cancellation_penalties?.free_cancellation_before ?? null;
  const isRefundable = freeBefore !== null;

  // Address
  const address = [hotel.address, hotel.cityName].filter(Boolean).join(", ");

  // Check-in / check-out times from Content API (already "HH:MM")
  const checkInTime = hotel.checkInTime || "14:00";
  const checkOutTime = hotel.checkOutTime || "12:00";

  // Description: beds + amenities + rate conditions
  const descriptionParts: string[] = [];
  if (hotel.description) descriptionParts.push(hotel.description);
  if (room.bedsLabel) descriptionParts.push(`Beds: ${room.bedsLabel}`);
  if (rate.amenities_data?.length) {
    descriptionParts.push(`Amenities: ${rate.amenities_data.join(", ")}`);
  }

  // Media: room photos (if any), fall back to hotel photos. Resolve {size} at save time
  // so the URLs stored in P2B are always ready to use without further transformation.
  const media = (room.photos.length > 0 ? room.photos : hotel.photos).map((url) =>
    toMediaItem(resolveWorldotaUrl(url), "ratehawk")
  );

  return {
    id: crypto.randomUUID(),
    type: "accommodation",
    title: `${hotel.name} - ${room.roomName}`,
    description: descriptionParts.join("\n\n"),
    address,
    location: hotel.cityName || hotel.address,
    start_date: checkInDate,
    end_date: checkOutDate,
    check_in_time: checkInTime,
    check_out_time: checkOutTime,
    check_in_note: "",
    cost: totalAmount,
    refundable_rate: isRefundable,
    price_tag: mealToTag(rate.meal),
    media,
    documentation: [],
    include_price: true,
    link: "",
    metadata: {
      ratehawk_rooms: selectedRooms.length,
      ratehawk_guests_per_room: selectedRooms.map((r) => ({
        adults: r.adults,
        childrenAges: r.childrenAges,
      })),
      hotel_metapolicy_struct: hotel.metapolicy_struct ?? null,
      hotel_metapolicy_extra_info: hotel.metapolicy_extra_info ?? null,
      taxes: resolveTaxData(rate)?.taxes ?? null,
    },
  };
}

// ─── Hotel policies (metapolicy_struct / metapolicy_extra_info) ──────────────

const METAPOLICY_LABELS: Record<string, string> = {
  check_in_check_out: "Check-in / Check-out",
  no_show: "No-show",
  deposit: "Deposit",
  add_fee: "Additional fees",
  pets: "Pets",
  cot: "Baby cot",
  extra_bed: "Extra bed",
  children: "Children",
  children_meal: "Children meal",
  meal: "Meal",
  internet: "Internet",
  parking: "Parking",
  shuttle: "Shuttle",
  visa: "Visa support",
};

function metapolicyLabel(key: string): string {
  return METAPOLICY_LABELS[key] ?? key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function isEmptyMetapolicyValue(value: unknown): boolean {
  if (value == null) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") {
    return Object.values(value as Record<string, unknown>).every(
      (v) => v == null || v === "unspecified" || (Array.isArray(v) && v.length === 0),
    );
  }
  return false;
}

function stringifyMetapolicyValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "object" && item !== null ? stringifyMetapolicyValue(item) : String(item)))
      .join("; ");
  }
  if (typeof value === "object" && value !== null) {
    return Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v != null && v !== "unspecified" && !(Array.isArray(v) && v.length === 0))
      .map(([k, v]) => {
        const formatted = Array.isArray(v) || typeof v === "object" ? stringifyMetapolicyValue(v) : String(v);
        return `${k.replace(/_/g, " ")}: ${formatted}`;
      })
      .join(", ");
  }
  return String(value);
}

/**
 * Flatten ETG's metapolicy_struct (shape varies per hotel/category) into a
 * generic list of non-empty {label, text} entries for display, without
 * hardcoding assumptions about each category's internal fields.
 */
export function formatMetapolicyEntries(
  struct: Record<string, unknown> | null | undefined,
): { key: string; label: string; text: string }[] {
  if (!struct) return [];
  return Object.entries(struct)
    .filter(([, value]) => !isEmptyMetapolicyValue(value))
    .map(([key, value]) => ({
      key,
      label: metapolicyLabel(key),
      text: stringifyMetapolicyValue(value),
    }));
}

export interface PolicyTextSegment {
  text: string;
  href?: string;
}

/**
 * Parse ETG's free-text policy notes, which may contain inline <a href="...">
 * anchors, into safe React-renderable segments — avoids dangerouslySetInnerHTML
 * on third-party content while preserving the intended links.
 */
export function parsePolicyText(raw: string): PolicyTextSegment[] {
  const segments: PolicyTextSegment[] = [];
  const anchorRe = /<a\s+[^>]*href=["']([^"']*)["'][^>]*>(.*?)<\/a>/gi;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = anchorRe.exec(raw)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ text: raw.slice(lastIndex, match.index) });
    }
    segments.push({ text: match[2].replace(/<[^>]+>/g, ""), href: match[1] });
    lastIndex = anchorRe.lastIndex;
  }
  if (lastIndex < raw.length) {
    segments.push({ text: raw.slice(lastIndex).replace(/<[^>]+>/g, "") });
  }
  return segments;
}
